import * as React from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Select,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";

import { ApiError, createTicket, listProjects, type CreatedTicket, type Project } from "../lib/api";
import { TITLE_MAX_LENGTH, hasMailItem, readSuggestion } from "../lib/mailitem";
import { recentTicketFor, rememberTicket, type RecentTicket } from "../lib/recent";
import { FIELD_DESCRIPTION, FIELD_PROJECT_ID, FIELD_TITLE, type Suggestion } from "../lib/suggestion";
import { SuggestionHint } from "./SuggestionHint";

const useStyles = makeStyles({
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  counter: { color: tokens.colorNeutralForeground3 },
  counterOver: { color: tokens.colorPaletteRedForeground1 },
  description: { minHeight: "160px" },
  centered: { display: "flex", justifyContent: "center", padding: "24px 0" },
});

export interface TicketFormProps {
  token: string;
  /** Called when the BFF reports the session is gone, so the pane can re-gate. */
  onSessionExpired: () => void;
}

export const TicketForm: React.FC<TicketFormProps> = ({ token, onSessionExpired }) => {
  const styles = useStyles();

  const [projects, setProjects] = React.useState<Project[] | null>(null);
  const [projectId, setProjectId] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  // Kept beyond the prefill: it is sent back with the ticket so the server can
  // record what the user changed about it.
  const [suggestion, setSuggestion] = React.useState<Suggestion | null>(null);
  const [duplicate, setDuplicate] = React.useState<RecentTicket | null>(null);

  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [prefillError, setPrefillError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [created, setCreated] = React.useState<CreatedTicket | null>(null);

  // A 401 anywhere means the sealed credential died; every caller reacts the
  // same way, so the check lives in one place.
  const handleFailure = React.useCallback(
    (err: unknown, setter: (message: string) => void) => {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionExpired();
        return;
      }
      setter(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    },
    [onSessionExpired]
  );

  React.useEffect(() => {
    let cancelled = false;
    listProjects(token)
      .then((list) => {
        if (cancelled) {
          return;
        }
        setProjects(list);
        // Preselecting the only option removes a click that has no decision
        // behind it.
        if (list.length === 1) {
          setProjectId(list[0].id);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          handleFailure(err, setLoadError);
          setProjects([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, handleFailure]);

  // Prefill runs separately from render so the form is usable — and testable
  // in a plain browser — before Office has a message to offer.
  React.useEffect(() => {
    let cancelled = false;
    if (!hasMailItem()) {
      return undefined;
    }
    readSuggestion()
      .then((proposal) => {
        if (cancelled) {
          return;
        }
        setSuggestion(proposal);
        // Only prefill empty fields: a re-render must never overwrite edits.
        setTitle((current) => current || (proposal.fields[FIELD_TITLE].value ?? ""));
        setDescription((current) => current || (proposal.fields[FIELD_DESCRIPTION].value ?? ""));
        setDuplicate(recentTicketFor(proposal.messageContext.messageId));
      })
      .catch(() => {
        if (!cancelled) {
          setPrefillError("Could not read this email. Fill the form in yourself.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedTitle = title.trim();
  const titleTooLong = title.length > TITLE_MAX_LENGTH;
  const canSubmit = !submitting && projectId !== "" && trimmedTitle !== "" && !titleTooLong;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ticket = await createTicket(
        token,
        {
          projectId,
          title: trimmedTitle,
          description: description.trim() || undefined,
        },
        // Sent as proposed, not as re-derived: the server compares the two
        // and records what the user changed.
        suggestion ?? undefined
      );
      if (suggestion) {
        rememberTicket(suggestion.messageContext.messageId, ticket.number);
      }
      setCreated(ticket);
    } catch (err) {
      handleFailure(err, setSubmitError);
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setCreated(null);
    setTitle("");
    setDescription("");
    // Filing the same email again is now a duplicate by definition, so the
    // warning that was absent a moment ago has to appear.
    if (suggestion) {
      setDuplicate(recentTicketFor(suggestion.messageContext.messageId));
    }
  };

  if (projects === null) {
    return (
      <div className={styles.centered}>
        <Spinner label="Loading projects…" />
      </div>
    );
  }

  if (created) {
    return (
      <div className={styles.form}>
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Ticket #{created.number} created</MessageBarTitle>
          </MessageBarBody>
        </MessageBar>
        <Button appearance="primary" onClick={startAnother}>
          Create another
        </Button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      {loadError && (
        <MessageBar intent="error">
          <MessageBarBody>{loadError}</MessageBarBody>
        </MessageBar>
      )}
      {prefillError && (
        <MessageBar intent="warning">
          <MessageBarBody>{prefillError}</MessageBarBody>
        </MessageBar>
      )}
      {/* A warning, never a block: filing the same email twice on purpose is
          legitimate — a second ticket for a second problem in one message. */}
      {duplicate && (
        <MessageBar intent="warning">
          <MessageBarBody>
            You already created ticket #{duplicate.number} from this email.
          </MessageBarBody>
        </MessageBar>
      )}
      {submitError && (
        <MessageBar intent="error">
          <MessageBarBody>{submitError}</MessageBarBody>
        </MessageBar>
      )}

      <Field label="Project" required>
        <Select value={projectId} onChange={(_, data) => setProjectId(data.value)} disabled={submitting}>
          <option value="">
            {projects.length === 0 ? "No active projects available" : "Select a project…"}
          </option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.key ? `${project.key} — ${project.name}` : project.name}
            </option>
          ))}
        </Select>
        <SuggestionHint field={suggestion?.fields[FIELD_PROJECT_ID]} current={projectId} />
      </Field>

      <Field
        label="Title"
        required
        validationState={titleTooLong ? "error" : "none"}
        validationMessage={
          <span className={titleTooLong ? styles.counterOver : styles.counter}>
            {title.length}/{TITLE_MAX_LENGTH}
          </span>
        }
      >
        <Input value={title} onChange={(_, data) => setTitle(data.value)} disabled={submitting} />
        <SuggestionHint field={suggestion?.fields[FIELD_TITLE]} current={title} />
      </Field>

      <Field label="Description">
        <Textarea
          className={styles.description}
          value={description}
          onChange={(_, data) => setDescription(data.value)}
          disabled={submitting}
          resize="vertical"
        />
        <SuggestionHint field={suggestion?.fields[FIELD_DESCRIPTION]} current={description} />
      </Field>

      <Button appearance="primary" type="submit" disabled={!canSubmit}>
        {submitting ? <Spinner size="tiny" label="Creating…" /> : "Create ticket"}
      </Button>
    </form>
  );
};
